import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#updateAccessibility();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  #updateAccessibility = () => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;
    const isDisabled = (isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop);
    const isAccordionMode = this.classList.contains('menu--accordion') && isMobile;

    if (isDisabled) {
      // Non-interactive mode - disable pointer interaction
      this.summary.style.pointerEvents = 'none';
      this.summary.setAttribute('aria-expanded', 'true');
    } else {
      // Interactive mode - enable interaction and update ARIA state
      this.summary.style.pointerEvents = '';
      if (isAccordionMode) {
        this.summary.setAttribute('aria-expanded', `${this.details.open}`);
      } else {
        this.summary.setAttribute('aria-expanded', 'true');
      }
    }
  };

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Close other accordion items in the same parent
    this.#closeOtherAccordions();

    // Sync ARIA state after the toggle occurs (only in accordion mode)
    const isAccordionMode = this.classList.contains('menu--accordion') && isMobile;
    if (isAccordionMode) {
      requestAnimationFrame(() => {
        this.summary.setAttribute('aria-expanded', `${this.details.open}`);
      });
    }
  };

  /**
   * Closes other accordion items in the same parent container
   */
  #closeOtherAccordions() {
    // Find the parent accordion container
    const parentAccordion = this.closest('.accordion');

    if (!parentAccordion) return;

    // Get all accordion-custom elements within the same parent
    const allAccordions = parentAccordion.querySelectorAll('accordion-custom');

    allAccordions.forEach((accordion) => {
      // Skip the current accordion
      if (accordion === this) return;

      // Close other accordions
      const details = accordion.querySelector('details');
      if (details && details.open) {
        details.open = false;

        // Update ARIA state if needed
        const summary = details.querySelector('summary');
        const isMobile = isMobileBreakpoint();
        if (summary && accordion.classList.contains('menu--accordion') && isMobile) {
          summary.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
    this.#updateAccessibility();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Prevent keyboard activation (Enter/Space) when disabled
    if (event.target === this.summary && (event.key === 'Enter' || event.key === ' ')) {
      if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
        event.preventDefault();
        return;
      }
    }

    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
